"use client";

import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import React from "react";
import SliderPrincipale from "@/components/Home/SliderPrincipale";
import PayloadHomepageHero, { getHomepageMediaUrl, type HomepageContent } from "@/components/Home/PayloadHomepageHero";
import PayloadServicesFilmstrip from "@/components/Home/PayloadServicesFilmstrip";
import PayloadPromoBanner from "@/components/Home/PayloadPromoBanner";
import PayloadLastMinuteOffers from "@/components/Home/PayloadLastMinuteOffers";
import PayloadRentToBuySection from "@/components/Home/PayloadRentToBuySection";
import PayloadCommercialVehiclesSection from "@/components/Home/PayloadCommercialVehiclesSection";
import PayloadSellCta from "@/components/Home/PayloadSellCta";
import BrandGrid from "@/components/Home/BrandGrid";
import type { VeicoloDataVeicolo } from "@/interface/VeicoloDataVeicolo";

// react-query + Redux store are ONLY needed for SearchAuto.
// Dynamic import (ssr: false) keeps them out of the critical-path bundle,
// saving ~720 KB compressed / 2.3 MB raw from the initial parse cost.
const DeferredSearchAuto = dynamic(
    () => import("@/components/Home/SearchAutoWithProviders"),
    {
        ssr: false,
        loading: () => <div aria-hidden="true" className="h-[200px] w-full rounded-2xl border border-white/60 bg-white/90 p-3 shadow-[0_4px_12px_rgba(11,27,51,0.14)] backdrop-blur-xl lg:h-[210px]">
            <div className="mb-3 h-4 w-40 animate-pulse rounded bg-slate-200" />
            <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
                {Array.from({ length: 4 }, (_, index) => <div key={index} className="h-10 animate-pulse rounded-lg bg-slate-100" />)}
            </div>
            <div className="mt-3 h-10 animate-pulse rounded-lg bg-slate-200" />
        </div>,
    },
);

interface HomeClientProps {
    offers: any[];
    banners: any[];
    homepageContent?: HomepageContent | null;
    rentToBuyVehicles?: VeicoloDataVeicolo[];
    rentToBuyVehicleCount?: number;
    commercialVehicles?: VeicoloDataVeicolo[];
    locations: any[];
    reviews: any[];
    brands: any[];
    searchAutoFilters: any;
    isB2B: boolean;
    useNewHomeSearch: boolean;
}

const DeferredLastMinute = dynamic(() => import("@/components/LastMinute"), {
    ssr: false,
    loading: () => <div className="min-h-[640px] bg-[#E1E7FF]" />,
});
const DeferredHomepageLocationsHero = dynamic(() => import("@/components/Home/PayloadHomepageLocationsHero"), {
    ssr: false,
    loading: () => <div className="min-h-[640px]" />,
});
const DeferredReview = dynamic(() => import("@/components/Review"), {
    ssr: false,
    loading: () => <div className="min-h-[520px] bg-[#F4F4F4]" />,
});

const B2BPage = React.memo(() => (
    <div className="w-full h-screen flex items-center justify-center bg-white">
        <Image
            src="/logo_consorzio.png"
            alt="Logo Consorzio"
            width={600}
            height={200}
            className="max-w-full h-auto"
            priority={true}
        />
    </div>
));

B2BPage.displayName = "B2BPage";

export default function HomeClient({
    offers,
    banners,
    homepageContent,
    rentToBuyVehicles = [],
    rentToBuyVehicleCount = 0,
    commercialVehicles = [],
    locations,
    reviews,
    brands,
    searchAutoFilters,
    isB2B,
    useNewHomeSearch,
}: HomeClientProps) {
    const [showDeferredSections, setShowDeferredSections] = React.useState(false);
    const [activeHomepageSlideIndex, setActiveHomepageSlideIndex] = React.useState(0);

    React.useEffect(() => {
        if (window.location.hash !== "#cerca-veicolo") return;

        const scrollToVehicleSearch = () => {
            const searchSection = Array.from(
                document.querySelectorAll<HTMLElement>("[data-home-vehicle-search]"),
            ).find((element) => element.getClientRects().length > 0);

            searchSection?.scrollIntoView({ behavior: "smooth", block: "start" });
        };

        requestAnimationFrame(() => requestAnimationFrame(scrollToVehicleSearch));
    }, []);

    React.useEffect(() => {
        const activate = () => setShowDeferredSections(true);

        if ("requestIdleCallback" in globalThis) {
            const idleId = globalThis.requestIdleCallback(activate, { timeout: 1500 });
            return () => globalThis.cancelIdleCallback(idleId);
        }

        const timeoutId = globalThis.setTimeout(activate, 1200);
        return () => globalThis.clearTimeout(timeoutId);
    }, []);

    if (isB2B) {
        return <B2BPage />;
    }

    const payloadReviews = homepageContent?.reviewsSection?.reviews?.map((review, index) => ({
        _id: review.id || `payload-review-${index}`,
        author: review.author,
        rating: Number(review.rating) || 0,
        reviewText: review.reviewText,
    })) || [];
    const homepageReviews = payloadReviews.length > 0 ? payloadReviews : reviews;

    const payloadLocations = homepageContent?.locationsSection?.cards?.map((location, index) => ({
        _id: location.id || `payload-location-${index}`,
        title: location.title,
        address: location.address,
        phone: location.phone || "",
        imgUrl: getHomepageMediaUrl(location.image, "original") || "",
        slug: location.slug || "",
        linkMaps: location.mapsUrl || "",
    })).filter((location) => Boolean(location.imgUrl)) || [];
    const homepageLocations = payloadLocations.length > 0 ? payloadLocations : locations;
    const homepageLocationsTitle = homepageContent?.locationsSection?.title || "Le nostre sedi";
    const homepageLocationsHeadline = homepageContent?.locationsSection?.headline || "Vicini a te, ovunque ti serva.";
    const homepageLocationsButton = homepageContent?.locationsSection?.buttonLabel || "Scopri la sede";

    const schemaOrgJsonLD = {
        "@context": "http://schema.org",
        "@type": "AutoDealer",
        name: "Gruppo Piccirillo",
        image: "https://www.gruppopiccirillo.it/logo.png",
        "@id": "https://www.gruppopiccirillo.it",
        url: "https://www.gruppopiccirillo.it",
        telephone: "+39-0823847911",
        address: {
            "@type": "PostalAddress",
            streetAddress: "VIA SANTELLA PARCO LA PERLA SNC",
            addressLocality: "SANTA MARIA CAPUA VETERE",
            addressRegion: "CE",
            postalCode: "81055",
            addressCountry: "IT",
        },
        priceRange: "$$$",
        sameAs: [
            "https://www.facebook.com/gruppopiccirillo",
            "https://www.instagram.com/gruppopiccirillo",
        ],
    };

    return (
        <>
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaOrgJsonLD) }}
            />

            {homepageContent?.slides?.length ? (
                <>
                    <PayloadHomepageHero
                        content={homepageContent}
                        activeIndex={activeHomepageSlideIndex}
                        onActiveIndexChange={setActiveHomepageSlideIndex}
                        searchPanel={(
                            <DeferredSearchAuto initialFilters={searchAutoFilters} useNewSearch compact />
                        )}
                    />
                </>
            ) : (
                <div className="relative min-h-[70vh]">
                    <SliderPrincipale banners={banners} />
                    <div
                        className={`absolute inset-0 hidden md:flex items-start justify-end z-20 px-4 pb-6 pointer-events-none ${
                            useNewHomeSearch ? "pt-12 lg:pt-16" : "pt-24 lg:pt-28"
                        }`}
                    >
                        <div data-home-vehicle-search className="w-full max-w-lg scroll-mt-4 xl:max-w-xl pointer-events-auto">
                            <DeferredSearchAuto initialFilters={searchAutoFilters} useNewSearch={useNewHomeSearch} />
                        </div>
                    </div>
                    <div className="mt-4 px-4 md:hidden">
                        <div data-home-vehicle-search className="mx-auto max-w-xl scroll-mt-4">
                            <DeferredSearchAuto initialFilters={searchAutoFilters} useNewSearch={useNewHomeSearch} />
                        </div>
                    </div>
                </div>
            )}

            {homepageContent ? (
                <>
                    <PayloadServicesFilmstrip content={homepageContent} />
                    <PayloadPromoBanner content={homepageContent} />
                    {homepageContent.offersSection ? (
                        <PayloadLastMinuteOffers content={homepageContent} offers={offers} />
                    ) : showDeferredSections ? (
                        <DeferredLastMinute offers={offers} />
                    ) : (
                        <div className="min-h-[640px] bg-[#E1E7FF]" />
                    )}
                    {homepageContent.rentToBuySection && (
                        <PayloadRentToBuySection content={homepageContent} vehicles={rentToBuyVehicles} vehicleCount={rentToBuyVehicleCount} />
                    )}
                    <PayloadCommercialVehiclesSection content={homepageContent} vehicles={commercialVehicles} />
                    <PayloadSellCta content={homepageContent} />
                </>
            ) : (
                <>
                    <div className="py-6 sm:py-10 lg:py-12">
                        <BrandGrid initialBrands={brands} />
                    </div>
                    {showDeferredSections ? (
                        <DeferredLastMinute offers={offers} />
                    ) : (
                        <div className="min-h-[640px] bg-[#E1E7FF]" />
                    )}
                </>
            )}

            {!homepageContent && <div className="max-w-7xl mx-auto bg-transparent px-4 py-6">
                <Link
                    href="/parco-veicoli?tipoVeicolo=Furgone"
                    className="block rounded-2xl bg-transparent overflow-hidden shadow-lg transition-transform hover:scale-[1.01]"
                    aria-label="Scopri i veicoli commerciali"
                >
                    <Image
                        src="/assets/Veicoli Commerciali Desktop Completo.webp"
                        alt="Veicoli commerciali Gruppo Piccirillo"
                        width={1600}
                        height={600}
                        className="hidden md:block w-full h-auto"
                    />
                    <Image
                        src="/assets/Veicoli Commerciali Mobile Completo.webp"
                        alt="Veicoli commerciali Gruppo Piccirillo"
                        width={800}
                        height={900}
                        className="md:hidden w-full h-auto"
                    />
                </Link>
            </div>}

            {homepageContent?.reviewsSection?.enabled !== false && (
                showDeferredSections ? (
                    <DeferredReview initialReviews={homepageReviews} content={homepageContent?.reviewsSection} />
                ) : (
                    <div className="min-h-[428px] bg-white md:min-h-[439px]" />
                )
            )}

            {homepageContent?.locationsSection?.enabled !== false && homepageLocations.length > 0 && (
                showDeferredSections ? (
                    <DeferredHomepageLocationsHero
                        locations={homepageLocations}
                        sectionTitle={homepageLocationsTitle}
                        headline={homepageLocationsHeadline}
                        buttonLabel={homepageLocationsButton}
                    />
                ) : (
                    <div className="min-h-[520px]" />
                )
            )}
        </>
    );
}
